import * as React from 'react';
import { useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import MobileStepper from '@mui/material/MobileStepper';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import SwipeableViews from 'react-swipeable-views-react-18-fix';
import { autoPlay } from 'react-swipeable-views-utils-react-18-fix';
import RelatedDocumentItem from 'pages/fileViewer/components/carousel/RelatedDocumentItem';
import PropTypes from 'prop-types';
import { useGetRelatedDocumentsQuery } from 'services/entityApi';
import { useIntl } from 'react-intl';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { LinearScale } from '@mui/icons-material';
import { Stack, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import MDButton from 'components/MDButton';

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

/**
 * RelatedDocumentsCarousel displays a carousel of related documents
 * for a given entity. It allows users to swipe through documents and
 * provides UI controls for navigation.
 *
 * @param {Object} props The properties for the component.
 * @param {number} props.entityId The ID of the entity for which related documents are fetched.
 * @param {Function} [props.onDocumentClick] A callback function triggered when a document is clicked.
 * @param {Object} [props.conf] Configuration object for the query to fetch related documents.
 * @param {string} [props.direction] - Direction of the carousel.
 * @param {Function}[props.onCarouselEmpty] - Callback for when the carousel is empty. Should accept a boolean argument.
 * @returns {null|JSX.Element} The RelatedDocumentsCarousel component.
 */
function RelatedDocumentsCarousel({ entityId, onDocumentClick, conf, direction, onCarouselEmpty }) {
  const theme = useTheme();
  const { formatMessage: msg } = useIntl();
  const [activeStep, setActiveStep] = React.useState(0);
  const lgWidthUp = useMediaQuery(theme.breakpoints.up('lg'));
  const vlWidthUp = useMediaQuery(theme.breakpoints.up('vl'));
  const chunkSize = computeChunkSize(direction);
  const scoreDisplayed = conf?.scoreDisplayed ?? false;
  const queryParams = {
    entityId,
    entityTypeCodes: conf?.entityTypeCodes ?? [],
    resultMax: conf?.resultMax ?? 10,
    scoreMin: conf?.scoreMin ?? 20,
    withTag: true
  };
  const { data, loading } = useGetRelatedDocumentsQuery(queryParams);
  const items = data?.content;
  const maxScore = items ? Math.max(...items.map((item) => item.score)) : 0;
  useEffect(() => {
    if (items?.length === 0) {
      onCarouselEmpty(true);
    } else {
      onCarouselEmpty(false);
    }
  }, [items, onCarouselEmpty]);

  const maxSteps = items ? Math.round(items.length / chunkSize) : 0;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  if (loading) return null;

  return items?.length > 0 ? (
    <MDBox sx={{ width: '100%', height: '100%' }} display="flex" flexDirection="column">
      <MDBox
        variant="gradient"
        bgColor="primary"
        borderRadius="lg"
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0
        }}
      >
        <LinearScale color="white" fontSize="large" />
        <MDTypography variant="h6" color="white" mx={1}>
          {msg({ id: 'relatedDocument.carousel.title' })}
        </MDTypography>
      </MDBox>
      <Box flex="1">
        <AutoPlaySwipeableViews
          autoplay={conf.autoSwipeInterval != null && conf.autoSwipeInterval > 0}
          axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
          index={activeStep}
          interval={conf.autoSwipeInterval}
          onChangeIndex={handleStepChange}
          enableMouseEvents
        >
          {chunks(items, chunkSize)?.map((chunk, index) => (
            <Stack key={index} direction={direction} spacing={2} flex={1}>
              {chunk.map((item, itemIndex) => (
                <MDBox flex={1} key={itemIndex}>
                  <RelatedDocumentItem
                    relatedDocument={item}
                    onDocumentClick={onDocumentClick}
                    scoreDisplayed={scoreDisplayed}
                    maxScore={maxScore}
                  />
                </MDBox>
              ))}
            </Stack>
          ))}
        </AutoPlaySwipeableViews>
      </Box>
      <MobileStepper
        steps={maxSteps}
        position="static"
        activeStep={activeStep}
        nextButton={
          <MDButton
            size="small"
            color="primary"
            onClick={handleNext}
            disabled={activeStep === maxSteps - 1}
          >
            {msg({ id: 'action.next' })}
            {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
          </MDButton>
        }
        backButton={
          <MDButton size="small" color="primary" onClick={handleBack} disabled={activeStep === 0}>
            {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            {msg({ id: 'action.previous' })}
          </MDButton>
        }
      />
    </MDBox>
  ) : null;

  function chunks(array, size) {
    const copy = array.slice();
    const results = [];
    while (copy.length) {
      results.push(copy.splice(0, size));
    }
    return results;
  }

  function computeChunkSize(direction) {
    if (direction === 'column') return 2;
    if (vlWidthUp) return 3;
    if (lgWidthUp) return 2;
    return 1;
  }
}

export default RelatedDocumentsCarousel;

RelatedDocumentsCarousel.defaultProps = {
  onDocumentClick: () => {},
  direction: 'row'
};

RelatedDocumentsCarousel.propTypes = {
  entityId: PropTypes.number.isRequired,
  onDocumentClick: PropTypes.func, // Expected: (file: object) => void
  conf: PropTypes.shape({
    entityTypeCodes: PropTypes.arrayOf(PropTypes.string),
    resultMax: PropTypes.number,
    scoreMin: PropTypes.number,
    scoreDisplayed: PropTypes.bool,
    autoSwipeInterval: PropTypes.number
  }),
  direction: PropTypes.string,
  onCarouselEmpty: PropTypes.func.isRequired // Expected: (isEmpty: bool) => void
};
